/**
 * MobileBrokerList Component
 *
 * Mobile page for displaying the list of brokers.
 * Route: /settings/portfolio/reference-data/brokers (mobile only)
 *
 * Pattern follows MobileAccountList with additions:
 * - ReferenceDataMobileHeader (Select-as-title, breadcrumbs)
 * - Popover on "New" button for dual action (Create New + Import from Catalog)
 * - BrokerImportModal integration with post-import navigation
 */

// 1. External dependencies
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Plus, Package } from 'lucide-react';

// 2. Workspace packages
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  EmptyState,
  Spinner,
  Popover,
  PopoverTrigger,
  PopoverContent,
  useDisclosure,
  useToastNotification,
} from '@enfomentis/ui';

// 3. Internal absolute imports
import { brokerService } from '@/services/BrokerService';

// 4. Internal — relative imports
import { useBrokers } from '../Brokers/context';
import { BrokerListItem, ReferenceDataMobileHeader } from '../components';
import { BrokerImportModal } from '../Brokers/BrokerImportModal';

export const MobileBrokerList: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToastNotification();

  const {
    brokers,
    filteredBrokers,
    loading,
    showArchived,
    setShowArchived,
    selectBroker,
    reloadBrokers,
  } = useBrokers();

  // Popover state for "New" button (matching BrokersTab pattern)
  const [isAddPopoverOpen, setIsAddPopoverOpen] = useState(false);

  // Empty state popover (separate instance)
  const [isEmptyPopoverOpen, setIsEmptyPopoverOpen] = useState(false);

  // Import modal state
  const importModalDisclosure = useDisclosure();

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleBrokerSelect = useCallback(
    (brokerId: string): void => {
      selectBroker(brokerId);
      navigate(`/settings/portfolio/reference-data/brokers/${brokerId}`);
    },
    [selectBroker, navigate]
  );

  const handleShowArchivedToggle = useCallback((): void => {
    setShowArchived(!showArchived);
  }, [showArchived, setShowArchived]);

  const handleCreateNew = useCallback((): void => {
    setIsAddPopoverOpen(false);
    setIsEmptyPopoverOpen(false);
    navigate('/settings/portfolio/reference-data/brokers/new');
  }, [navigate]);

  const handleImportFromCatalog = useCallback((): void => {
    setIsAddPopoverOpen(false);
    setIsEmptyPopoverOpen(false);
    importModalDisclosure.onOpen();
  }, [importModalDisclosure]);

  /**
   * Post-import handler: reload brokers, verify existence, select, and navigate.
   * Must never rethrow — modal calls onImportComplete without await.
   */
  const handleImportComplete = useCallback(
    (brokerId: string): void => {
      (async (): Promise<void> => {
        try {
          await reloadBrokers();
          const broker = await brokerService.get(brokerId);
          if (broker) {
            selectBroker(brokerId);
            navigate(`/settings/portfolio/reference-data/brokers/${brokerId}`);
          } else {
            showToast({
              title: 'Navigation Failed',
              description: 'Broker was imported but could not be found. Please select it from the list.',
              color: 'warning',
            });
          }
        } catch {
          showToast({
            title: 'Navigation Failed',
            description: 'Broker was imported but could not navigate to it. Please select it from the list.',
            color: 'warning',
          });
        }
      })();
    },
    [reloadBrokers, selectBroker, navigate, showToast]
  );

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------

  const hasBrokers = brokers.length > 0;
  const hasFilteredBrokers = filteredBrokers.length > 0;

  // ---------------------------------------------------------------------------
  // Shared popover content (used by both header and empty state popovers)
  // ---------------------------------------------------------------------------

  const renderPopoverButtons = (): React.ReactNode => (
    <PopoverContent className="p-1">
      <div className="flex flex-col min-w-40">
        <Button
          size="md"
          variant="light"
          className="justify-start"
          startContent={<Plus size={16} />}
          onPress={handleCreateNew}
        >
          Create New...
        </Button>
        <Button
          size="md"
          variant="light"
          className="justify-start"
          startContent={<Package size={16} />}
          onPress={handleImportFromCatalog}
        >
          Import from Catalog...
        </Button>
      </div>
    </PopoverContent>
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="flex flex-col h-full">
      {/* Reference Data Header — Select-as-title switcher */}
      <ReferenceDataMobileHeader activeSection="brokers" />

      {/* Main Broker List Card */}
      <Card radius="none" shadow="none" className="flex-1 min-h-0 flex flex-col">
        {/* Header */}
        <CardHeader className="flex items-center justify-between pb-1 border-b border-default-200">
          {/* Left: Archive toggle */}
          <Button
            size="sm"
            variant="light"
            startContent={showArchived ? <Eye size={16} /> : <EyeOff size={16} />}
            onPress={handleShowArchivedToggle}
            className={showArchived ? 'text-text-primary' : 'text-text-tertiary'}
          >
            Archived
          </Button>

          {/* Right: New button with Popover */}
          <Popover
            isOpen={isAddPopoverOpen}
            onOpenChange={setIsAddPopoverOpen}
            placement="bottom-end"
          >
            <PopoverTrigger>
              <Button
                size="sm"
                variant="light"
                startContent={<Plus size={16} />}
              >
                New
              </Button>
            </PopoverTrigger>
            {renderPopoverButtons()}
          </Popover>
        </CardHeader>

        {/* Content */}
        <CardBody className="flex-1 min-h-0 overflow-y-auto px-1 pb-2">
          {/* Loading state */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              <Spinner size="lg" />
              <p className="text-text-secondary text-sm">Loading brokers...</p>
            </div>
          )}

          {/* Empty state - No brokers */}
          {!hasBrokers && !loading && (
            <EmptyState
              title="No brokers yet"
              description="Add your first broker to track fee structures and accounts."
              action={
                <Popover
                  isOpen={isEmptyPopoverOpen}
                  onOpenChange={setIsEmptyPopoverOpen}
                  placement="bottom"
                >
                  <PopoverTrigger>
                    <Button
                      variant="bordered"
                      startContent={<Plus size={16} />}
                    >
                      New Broker
                    </Button>
                  </PopoverTrigger>
                  {renderPopoverButtons()}
                </Popover>
              }
            />
          )}

          {/* Empty state - All archived */}
          {hasBrokers && !hasFilteredBrokers && !loading && (
            <EmptyState
              title="No active brokers"
              description="All brokers are archived"
              action={
                <Button
                  variant="flat"
                  startContent={<Eye size={16} />}
                  onPress={handleShowArchivedToggle}
                >
                  Show Archived
                </Button>
              }
            />
          )}

          {/* Broker list */}
          {!loading && hasFilteredBrokers && (
            <div className="space-y-2">
              {filteredBrokers.map((broker) => (
                <BrokerListItem
                  key={broker.id}
                  broker={broker}
                  onSelect={() => handleBrokerSelect(broker.id)}
                />
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Import Modal */}
      <BrokerImportModal
        isOpen={importModalDisclosure.isOpen}
        onClose={importModalDisclosure.onClose}
        onImportComplete={handleImportComplete}
        existingBrokers={brokers}
        loading={loading}
      />
    </div>
  );
};
